import { FC } from 'react';

import { NodeStatus } from '@dbt-labs/dbt-dag';
import {
  Badge,
  BadgeType,
  Ryecon,
  RyeconDuotoneStale,
  RyeconDuotoneUnknown,
  RyeconStatusError,
  RyeconStatusReused,
  RyeconStatusSkipped,
  RyeconStatusSuccess,
  RyeconStatusWarning,
  Tooltip,
} from '@dbt-labs/sourdough';

import { FreshnessStatus } from '@/__generated__/graphql';
import { toTitleCase } from '@/util/string';

export type NodeBadgeStatus =
  | FreshnessStatus
  | NodeStatus
  | 'not available'
  | 'true'
  | 'false';

interface NodeStatusIconBadgeProps {
  status: NodeBadgeStatus;
  className?: string;
  size?: 'small' | 'standard';
  tooltip?: string;
}

const oldNodeStatusSizeToNewSize = (size: 'small' | 'standard'): 'sm' | 'lg' => {
  return size === 'small' ? 'sm' : 'lg';
};

const STATUS_TO_TYPE_MAP: Record<NodeBadgeStatus, BadgeType> = {
  // Run statuses
  reused: 'teal',
  success: 'success',
  error: 'error',
  skipped: 'default',

  // Test statuses
  pass: 'success',
  fail: 'error',
  warn: 'warning',

  // Freshness statuses
  [FreshnessStatus.Pass]: 'success',
  [FreshnessStatus.Warn]: 'warning',
  [FreshnessStatus.Error]: 'error',
  [FreshnessStatus.Outdated]: 'default',
  [FreshnessStatus.Unconfigured]: 'default',
  [FreshnessStatus.Skipped]: 'default',
  [FreshnessStatus.Unknown]: 'default',

  // Other statuses
  'runtime error': 'error',
  unknown: 'default',
  unconfigured: 'default',
  outdated: 'default',
  'not available': 'default',
  true: 'default',
  false: 'default',
};

const STATUS_TO_RYECON_MAP: Record<NodeBadgeStatus, Ryecon | undefined> = {
  // Run statuses
  reused: RyeconStatusReused,
  success: RyeconStatusSuccess,
  error: RyeconStatusError,
  skipped: RyeconStatusSkipped,

  // Test statuses
  pass: RyeconStatusSuccess,
  fail: RyeconStatusError,
  warn: RyeconStatusWarning,

  // Freshness statuses
  [FreshnessStatus.Pass]: RyeconStatusSuccess,
  [FreshnessStatus.Warn]: RyeconStatusWarning,
  [FreshnessStatus.Error]: RyeconStatusError,
  [FreshnessStatus.Outdated]: RyeconDuotoneStale,
  [FreshnessStatus.Skipped]: RyeconStatusSkipped,
  [FreshnessStatus.Unknown]: RyeconDuotoneUnknown,
  [FreshnessStatus.Unconfigured]: RyeconDuotoneStale,

  // Other statuses
  'runtime error': RyeconStatusError,
  unknown: RyeconDuotoneUnknown,
  unconfigured: RyeconDuotoneStale,
  outdated: RyeconDuotoneStale,

  'not available': undefined,
  true: undefined,
  false: undefined,
};

const DECORATIVE_TEXT_STATUSES = new Set<NodeBadgeStatus>([
  FreshnessStatus.Outdated,
  'outdated',
  FreshnessStatus.Unknown,
  'unknown',
  FreshnessStatus.Unconfigured,
  'unconfigured',
]);

export const NodeStatusIconBadge: FC<NodeStatusIconBadgeProps> = ({
  status,
  size,
  className,
  tooltip,
}) => {
  const decorativeClass = DECORATIVE_TEXT_STATUSES.has(status)
    ? '[&_*:not(.ryecon-accent)]:!text-fgDecorative'
    : '';
  return (
    <Tooltip content={tooltip}>
      <Badge
        size={oldNodeStatusSizeToNewSize(size ?? 'standard')}
        type={STATUS_TO_TYPE_MAP[status]}
        ryecon={STATUS_TO_RYECON_MAP[status]}
        className={`${decorativeClass} ${className ?? ''}`.trim()}
        text={toTitleCase(status)}
      />
    </Tooltip>
  );
};
